import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus,
  Mail,
  RotateCcw,
  Ban,
  Shield,
  ShieldCheck,
  Trash2,
  Users,
  CalendarCheck,
  Clock,
  ChevronDown,
  Crown,
  LogOut,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";

const roleLabel = (r) =>
  r === "owner" ? "Owner" : r === "co_owner" ? "Co-owner" : "Member";

const statusPill = (s) => {
  switch (s) {
    case "pending":
      return "border-signalAmber/50 text-signalAmber";
    case "accepted":
      return "border-signalGreen/50 text-signalGreen";
    case "expired":
    case "revoked":
      return "border-rule text-mist";
    default:
      return "border-rule text-mist";
  }
};

const emailStatusLabel = (s) => {
  if (!s) return null;
  switch (s) {
    case "queued":
      return "Queued";
    case "sent":
      return "Sent";
    case "delivered":
      return "Delivered";
    case "opened":
      return "Opened";
    case "clicked":
      return "Clicked";
    case "bounced":
      return "Bounced";
    case "complained":
      return "Complained";
    case "failed":
      return "Failed";
    default:
      return s;
  }
};

export default function Household() {
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});

  const load = async () => {
    try {
      const res = await api.get("/household");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load household.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const isAdmin = data?.is_admin_of_household;
  const isOwner = data?.is_owner;

  const submitInvite = async (e) => {
    e?.preventDefault?.();
    const email = inviteEmail.trim().toLowerCase();
    if (!email) return;
    setInviteBusy(true);
    try {
      await api.post("/household/invites", { email, role: inviteRole });
      toast.success(`Invitation sent to ${email}.`);
      setInviteEmail("");
      setInviteRole("member");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send invite.";
      toast.error(typeof msg === "string" ? msg : "Could not send invite.");
    } finally {
      setInviteBusy(false);
    }
  };

  const withBusy = async (key, fn) => {
    setRowBusy((s) => ({ ...s, [key]: true }));
    try {
      await fn();
    } finally {
      setRowBusy((s) => ({ ...s, [key]: false }));
    }
  };

  const resend = (id) =>
    withBusy(`resend-${id}`, async () => {
      try {
        await api.post(`/household/invites/${id}/resend`);
        toast.success("Invitation re-sent.");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not resend.");
      }
    });

  const revoke = (id) =>
    withBusy(`revoke-${id}`, async () => {
      if (!window.confirm("Revoke this invitation?")) return;
      try {
        await api.post(`/household/invites/${id}/revoke`);
        toast.success("Invitation revoked.");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not revoke.");
      }
    });

  const promote = (uid, role) =>
    withBusy(`role-${uid}`, async () => {
      try {
        await api.post(`/household/members/${uid}/role`, { role });
        toast.success(`Role updated to ${roleLabel(role)}.`);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not update role.");
      }
    });

  const removeMember = (uid) =>
    withBusy(`remove-${uid}`, async () => {
      if (!window.confirm("Remove this member from the household?")) return;
      try {
        await api.delete(`/household/members/${uid}`);
        toast.success("Member removed.");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not remove.");
      }
    });

  const leave = () =>
    withBusy("leave", async () => {
      if (!window.confirm("Leave this household? Your calendar contributions will be removed from its aggregate.")) return;
      try {
        await api.post("/household/leave");
        toast.success("You’ve left the household.");
        await checkAuth();
        window.location.href = "/app/onboarding";
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not leave.");
      }
    });

  const totals = useMemo(() => {
    if (!data) return { members: 0, connected: 0, pending: 0 };
    return {
      members: data.members.length,
      connected: data.members.filter((m) => m.calendar_connected).length,
      pending: data.invites.filter((i) => i.status === "pending").length,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center font-mono text-[11px] uppercase tracking-[0.24em] text-mist">
        Loading household…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div data-testid="household-page" className="mx-auto max-w-6xl px-6 py-10 md:px-10 md:py-14">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 border-b border-rule pb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">
            {data.household_name}
          </p>
          <h1 className="mt-3 font-display text-4xl leading-tight text-ink md:text-5xl">
            Household roster.
          </h1>
          <p className="mt-4 max-w-2xl text-base text-mist">
            Everyone whose calendar and load is folded into your Life OS signals.
            {isOwner
              ? " You’re the owner — you can invite anyone, promote co-owners, or remove members."
              : isAdmin
                ? " You’re a co-owner — you can invite new members. Only the original owner can promote or remove."
                : " You’re a member — your calendar contributes to the household. You can leave at any time."}
          </p>
        </div>
        <div className="hidden shrink-0 grid-cols-3 gap-3 md:grid">
          <Stat label="Members" value={totals.members} icon={Users} />
          <Stat label="Connected" value={totals.connected} icon={CalendarCheck} tone="teal" />
          <Stat label="Pending" value={totals.pending} icon={Clock} tone={totals.pending ? "amber" : undefined} />
        </div>
      </div>

      {/* Invite form */}
      {isAdmin && (
        <section className="mt-10 border border-rule bg-deep/40 p-6 md:p-8" data-testid="household-invite-card">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-teal">
            <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
            Invite a household member
          </div>
          <form onSubmit={submitInvite} className="mt-5 flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                Email address
              </label>
              <input
                type="email"
                required
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="partner@example.com"
                data-testid="invite-email-input"
                className="mt-2 w-full border border-rule bg-obsidian px-4 py-3 text-ink placeholder:text-mist/50 focus:border-teal focus:outline-none"
              />
            </div>
            {isOwner && (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
                  Role
                </label>
                <div className="relative mt-2">
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    data-testid="invite-role-select"
                    className="w-40 appearance-none border border-rule bg-obsidian px-4 py-3 pr-10 text-ink focus:border-teal focus:outline-none"
                  >
                    <option value="member">Member</option>
                    <option value="co_owner">Co-owner</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-mist" strokeWidth={1.5} />
                </div>
              </div>
            )}
            <button
              type="submit"
              disabled={inviteBusy}
              data-testid="invite-submit-btn"
              className="group inline-flex items-center gap-2 bg-teal px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-teal-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
              {inviteBusy ? "Dispatching…" : "Send invite"}
            </button>
          </form>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
            Invite email arrives from Life OS · valid for 14 days · magic-link sign-in
          </p>
        </section>
      )}

      {/* Members list */}
      <section className="mt-10">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
          <Users className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
          On the roster · {totals.members}
        </div>
        <ul className="mt-4 divide-y divide-rule border border-rule bg-deep/30">
          {data.members.map((m) => (
            <li
              key={m.user_id}
              data-testid={`member-row-${m.user_id}`}
              className="grid grid-cols-1 gap-3 px-5 py-4 md:grid-cols-12 md:items-center"
            >
              <div className="flex items-center gap-3 md:col-span-5">
                {m.picture ? (
                  <img src={m.picture} alt="" className="h-9 w-9 border border-rule object-cover" />
                ) : (
                  <div className="flex h-9 w-9 items-center justify-center border border-rule bg-obsidian font-mono text-[11px] text-teal">
                    {(m.name || m.email || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-ink">
                    <span className="truncate">{m.name || m.email}</span>
                    {m.is_me && (
                      <span className="border border-teal/60 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.22em] text-teal">
                        you
                      </span>
                    )}
                  </div>
                  <div className="truncate font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                    {m.email}
                  </div>
                </div>
              </div>
              <div className="md:col-span-3">
                <span
                  className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${
                    m.role === "owner"
                      ? "border-amber/60 text-amber"
                      : m.role === "co_owner"
                        ? "border-teal/60 text-teal"
                        : "border-rule text-mist"
                  }`}
                >
                  {m.role === "owner" ? <Crown className="h-3 w-3" strokeWidth={1.5} /> : m.role === "co_owner" ? <ShieldCheck className="h-3 w-3" strokeWidth={1.5} /> : <Shield className="h-3 w-3" strokeWidth={1.5} />}
                  {roleLabel(m.role)}
                </span>
              </div>
              <div className="md:col-span-2">
                <span
                  className={`inline-flex items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${
                    m.calendar_connected ? "border-signalGreen/50 text-signalGreen" : "border-rule text-mist"
                  }`}
                >
                  <CalendarCheck className="h-3 w-3" strokeWidth={1.5} />
                  {m.calendar_connected ? "Cal · live" : "No cal"}
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 md:col-span-2">
                {isOwner && m.role !== "owner" && (
                  <>
                    {m.role === "member" ? (
                      <button
                        onClick={() => promote(m.user_id, "co_owner")}
                        disabled={rowBusy[`role-${m.user_id}`]}
                        data-testid={`promote-${m.user_id}`}
                        title="Promote to co-owner"
                        className="border border-rule p-2 text-mist transition-colors duration-300 hover:border-teal hover:text-ink disabled:opacity-40"
                      >
                        <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    ) : (
                      <button
                        onClick={() => promote(m.user_id, "member")}
                        disabled={rowBusy[`role-${m.user_id}`]}
                        data-testid={`demote-${m.user_id}`}
                        title="Demote to member"
                        className="border border-rule p-2 text-mist transition-colors duration-300 hover:border-amber hover:text-ink disabled:opacity-40"
                      >
                        <Shield className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </button>
                    )}
                    <button
                      onClick={() => removeMember(m.user_id)}
                      disabled={rowBusy[`remove-${m.user_id}`]}
                      data-testid={`remove-${m.user_id}`}
                      title="Remove from household"
                      className="border border-rule p-2 text-mist transition-colors duration-300 hover:border-signalRed hover:text-signalRed disabled:opacity-40"
                    >
                      <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                    </button>
                  </>
                )}
                {m.is_me && m.role !== "owner" && (
                  <button
                    onClick={leave}
                    disabled={rowBusy["leave"]}
                    data-testid="leave-household-btn"
                    className="inline-flex items-center gap-1.5 border border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-amber hover:text-ink"
                  >
                    <LogOut className="h-3 w-3" strokeWidth={1.5} />
                    Leave
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Invites list */}
      {isAdmin && (
        <section className="mt-12">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Mail className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Invitations
          </div>
          {data.invites.length === 0 ? (
            <div className="mt-4 border border-dashed border-rule bg-deep/20 p-8 text-center text-sm text-mist" data-testid="no-invites">
              No invitations yet. Send one above to add a member.
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-rule border border-rule bg-deep/30">
              {data.invites.map((inv) => (
                <li
                  key={inv.invite_id}
                  data-testid={`invite-row-${inv.invite_id}`}
                  className="grid grid-cols-1 gap-3 px-5 py-4 md:grid-cols-12 md:items-center"
                >
                  <div className="md:col-span-4">
                    <div className="truncate text-ink" data-testid={`invite-email-${inv.invite_id}`}>
                      {inv.email}
                    </div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                      as {roleLabel(inv.role)} · sent {new Date(inv.invited_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <span
                      className={`inline-flex items-center border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${statusPill(inv.status)}`}
                      data-testid={`invite-status-${inv.invite_id}`}
                    >
                      {inv.status}
                    </span>
                  </div>
                  <div className="md:col-span-3">
                    {inv.email_status && (
                      <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                        Email · <span className="text-ink">{emailStatusLabel(inv.email_status)}</span>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2 md:col-span-3">
                    {inv.status === "pending" && (
                      <>
                        <button
                          onClick={() => resend(inv.invite_id)}
                          disabled={rowBusy[`resend-${inv.invite_id}`]}
                          data-testid={`resend-${inv.invite_id}`}
                          title="Resend invitation"
                          className="inline-flex items-center gap-1.5 border border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink"
                        >
                          <RotateCcw className="h-3 w-3" strokeWidth={1.5} />
                          Resend
                        </button>
                        <button
                          onClick={() => revoke(inv.invite_id)}
                          disabled={rowBusy[`revoke-${inv.invite_id}`]}
                          data-testid={`revoke-${inv.invite_id}`}
                          title="Revoke invitation"
                          className="inline-flex items-center gap-1.5 border border-rule px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-signalRed hover:text-signalRed"
                        >
                          <Ban className="h-3 w-3" strokeWidth={1.5} />
                          Revoke
                        </button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, tone }) {
  const color =
    tone === "teal"
      ? "text-signalGreen"
      : tone === "amber"
        ? "text-signalAmber"
        : "text-ink";
  return (
    <div className="border border-rule bg-deep/40 px-4 py-3">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
        {Icon && <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />}
        {label}
      </div>
      <div className={`mt-2 font-mono text-2xl ${color}`}>{value}</div>
    </div>
  );
}
